import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";

export default function InvoiceSettings() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const { data } = useQuery({ queryKey: ["inv-settings-full"], queryFn: async () => (await api.get("/invoices/settings")).data });
  useEffect(() => { if (data && !f) setF(data); }, [data, f]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/invoices/settings", {
      gst_rate: Number(f.gst_rate), default_payment_terms: f.default_payment_terms,
      bank_details: f.bank_details, invoice_footer: f.invoice_footer, website: f.website, currency: f.currency,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["inv-settings"] }); toast.success("Invoice settings saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!f) return null;
  const co = f.company || {};

  return (
    <div data-testid="invoice-settings-page">
      <button onClick={() => navigate("/invoices")} className="mb-3 flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-to-invoices"><ArrowLeft className="h-4 w-4" /> Invoices</button>
      <PageHeader title="Invoice Settings" subtitle="Company details, GST, numbering and payment configuration." />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
          <SectionTitle className="mb-3">Company (from Admin Settings)</SectionTitle>
          <div className="space-y-1 text-sm text-slate-300">
            <p><span className="text-slate-500">Legal name:</span> {co.legal_name || "—"}</p>
            <p><span className="text-slate-500">ABN:</span> {co.abn || "—"} · <span className="text-slate-500">ACN:</span> {co.acn || "—"}</p>
            <p><span className="text-slate-500">Address:</span> {co.address || "—"}</p>
            <p><span className="text-slate-500">GST registered:</span> {co.gst_registered ? "Yes (TAX INVOICE)" : "No (INVOICE)"}</p>
          </div>
          <p className="mt-2 text-xs text-slate-500">Edit company identity in Settings → Company.</p>
        </div>

        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
          <SectionTitle className="mb-3">Invoice configuration</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-400">GST rate (%)</Label><Input type="number" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="gst-rate-input" /></div>
            <div><Label className="text-xs text-slate-400">Currency</Label><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="currency-input" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-400">Default payment terms</Label><Input value={f.default_payment_terms} onChange={(e) => setF({ ...f, default_payment_terms: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="terms-input" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-400">Website</Label><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="website-input" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-400">Bank / payment details</Label><Textarea rows={2} value={f.bank_details} onChange={(e) => setF({ ...f, bank_details: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="bank-details-input" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-400">Invoice footer</Label><Input value={f.invoice_footer} onChange={(e) => setF({ ...f, invoice_footer: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="footer-input" /></div>
          </div>
          <p className="mt-3 text-xs text-slate-500">Numbering prefix: <span className="font-mono text-slate-300">{f.prefix}-YYYY-####</span></p>
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="mt-4 bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="save-settings-btn"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
        </div>
      </div>
    </div>
  );
}
